import { PpssPerformanceComponent } from '@products/fund-listing/ppss/ppss-performance/ppss-performance.component';
import { AgGridHelperService } from '@products/fund-listing/services/ag-grid-helper.service';
import { TranslateService } from '@shared/translate/translate.service';
import { PPSSState } from '@types';
import { ColGroupDef } from '@ag-grid-community/core';
import { PpssPriceComponent } from '@products/fund-listing/ppss/ppss-price/ppss-price.component';

/**
 * Return performance column definitions for US 529 Portfolios.
 * @param ppssTab ppss tab component
 * @param state ppss service state
 * @param agGridHelperService ag grid helper service instance
 */
export function getPerformanceColumnDefsForUS_NjBest(
  ppssTab: PpssPerformanceComponent,
  state: PPSSState,
  agGridHelperService: AgGridHelperService,
  translateService: TranslateService
): ColGroupDef[] {
  const colGroupDefs: ColGroupDef[] = [
    {
      groupId: 'FUND',
      children: [
        agGridHelperService.getFundNameNjBestColDef(
          false,
          false,
          null,
          ppssTab.productType,
          false
        ),
      ],
    },
    {
      groupId: 'NAV',
      headerGroupComponent: 'dateGroupHeaderComponent',
      headerGroupComponentParams: {
        asOfDateText: () => ppssTab.navAsOfDate,
      },
      children: [
        agGridHelperService.getNavYTDReturnNjBestColDef({
          headerName: translateService.instant(
            'products.ytd-total-returns-nav'
          ),
          minWidth: 150,
        }),
      ],
    },
    {
      groupId: 'PERFORMANCE',
      headerGroupComponent: 'performanceGroupHeaderComponent',
      headerGroupComponentParams: {
        perfTable: ppssTab,
      },
      children: [
        agGridHelperService.getAvgAnnualMonthly1YrColDef(
          state.config.suppressDetails
        ),
        agGridHelperService.getAvgAnnualMonthly3YrColDef({ minWidth: 150 }),
        agGridHelperService.getAvgAnnualMonthly5YrColDef({ minWidth: 150 }),
        agGridHelperService.getAvgAnnualMonthly10YrColDef({ minWidth: 150 }),
        agGridHelperService.getAvgAnnualMonthlyInceptionColDef(
          state.config.suppressDetails,
          {
            minWidth: 150,
          }
        ),
        // Average Annual Quaterly colDefs.
        agGridHelperService.getAvgAnnualQuarterly1YrColDef(
          state.config.suppressDetails,
          {
            width: 150,
          }
        ),
        agGridHelperService.getAvgAnnualQuarterly3YrColDef({
          width: 150,
        }),
        agGridHelperService.getAvgAnnualQuarterly5YrColDef({
          width: 150,
        }),
        agGridHelperService.getAvgAnnualQuarterly10YrColDef({
          width: 150,
        }),
        agGridHelperService.getAvgAnnualQuarterlyInceptionColDef(
          state.config.suppressDetails,
          {
            width: 150,
          }
        ),

        // Cumulative Monthly
        agGridHelperService.getCumulativeMonthly1YrColDef(
          state.config.suppressDetails,
          {},
          state.isNjBest
        ),
        agGridHelperService.getCumulativeMonthly3YrColDef(
          { minWidth: 150 },
          state.isNjBest
        ),
        agGridHelperService.getCumulativeMonthly5YrColDef(
          { minWidth: 150 },
          state.isNjBest
        ),
        agGridHelperService.getCumulativeMonthly10YrColDef(
          { minWidth: 150 },
          state.isNjBest
        ),
        agGridHelperService.getCumulativeMonthlyInceptionColDef(
          state.config.suppressDetails,
          { minWidth: 150 },
          state.isNjBest
        ),

        // Cumulative Quarterly
        agGridHelperService.getCumulativeQuarterly1YrColDef(
          state.config.suppressDetails,
          {
            minWidth: 150,
          },
          state.isNjBest
        ),
        agGridHelperService.getCumulativeQuarterly3YrColDef(
          {
            minWidth: 150,
          },
          state.isNjBest
        ),
        agGridHelperService.getCumulativeQuarterly5YrColDef(
          {
            minWidth: 150,
          },
          state.isNjBest
        ),
        agGridHelperService.getCumulativeQuarterly10YrColDef(
          {
            minWidth: 150,
          },
          state.isNjBest
        ),
        agGridHelperService.getCumulativeQuarterlyInceptionColDef(
          state.config.suppressDetails,
          {
            minWidth: 150,
          },
          state.isNjBest
        ),

        // Discrete Annual Monthly
        agGridHelperService.getDiscreteMonthly1YrColDef(
          state.config.suppressDetails,
          {
            minWidth: 175,
            headerName: translateService.instant('pds.CUMULATIVE_RET_1YR'),
          },
          state.isNjBest
        ),
        agGridHelperService.getDiscreteMonthly3YrColDef(
          {
            minWidth: 175,
            headerName: translateService.instant('pds.CUMULATIVE_RET_3YR'),
          },
          state.isNjBest
        ),
        agGridHelperService.getDiscreteMonthly5YrColDef(
          {
            minWidth: 175,
            headerName: translateService.instant('pds.CUMULATIVE_RET_5YR'),
          },
          state.isNjBest
        ),
        agGridHelperService.getDiscreteMonthly10YrColDef(
          {
            minWidth: 175,
            headerName: translateService.instant('pds.CUMULATIVE_RET_10YR'),
          },
          state.isNjBest
        ),

        // Discrete Annual Quaterly
        agGridHelperService.getDiscreteQuarterly1YrColDef(
          state.config.suppressDetails,
          {
            minWidth: 175,
            headerName: translateService.instant('pds.CUMULATIVE_RET_1YR'),
          },
          state.isNjBest
        ),
        agGridHelperService.getDiscreteQuarterly3YrColDef(
          {
            minWidth: 175,
            headerName: translateService.instant('pds.CUMULATIVE_RET_3YR'),
          },
          state.isNjBest
        ),
        agGridHelperService.getDiscreteQuarterly5YrColDef(
          {
            minWidth: 175,
            headerName: translateService.instant('pds.CUMULATIVE_RET_5YR'),
          },
          state.isNjBest
        ),
        agGridHelperService.getDiscreteQuarterly10YrColDef(
          {
            minWidth: 175,
            headerName: translateService.instant('pds.CUMULATIVE_RET_10YR'),
          },
          state.isNjBest
        ),
      ],
    },
  ];
  ppssTab.calcTypeParams = colGroupDefs.find(
    (colGroupDef: ColGroupDef): boolean => colGroupDef.groupId === 'FUND'
  ).headerGroupComponentParams;
  ppssTab.performanceParams = colGroupDefs.find(
    (colGroupDef: ColGroupDef): boolean => colGroupDef.groupId === 'PERFORMANCE'
  ).headerGroupComponentParams;
  return colGroupDefs;
}

export function getPriceColumnDefsForUS_NjBest(
  ppssTab: PpssPriceComponent,
  state: PPSSState,
  agGridHelperService: AgGridHelperService,
  translateService: TranslateService
): ColGroupDef[] {
  const colGroupDefs: ColGroupDef[] = [
    {
      groupId: 'FUND',
      children: [
        agGridHelperService.getFundNameNjBestColDef(
          false,
          false,
          null,
          ppssTab.productType,
          false
        ),
      ],
    },
    {
      groupId: 'PRICE',
      headerGroupComponent: 'priceGroupHeaderComponent',
      headerGroupComponentParams: {
        perfTable: ppssTab,
      },
      children: [
        agGridHelperService.getNavValueColDef(),
        agGridHelperService.get52WeekHighColDef(),
        agGridHelperService.get52WeekLowColDef(),
        agGridHelperService.getPortfolioNumColDef(),
      ],
    },
  ];

  return colGroupDefs;
}
